// src/app/modules/salles/SalleManagement.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Plus, Search, Edit2, Trash2, Save, X, MapPin, Monitor } from "lucide-react";

import {
    fetchSallesRequest,
    createSalleRequest,
    updateSalleRequest,
    deleteSalleRequest,
} from "../../../../redux/actions/sallesActions";

import "../RessourcesStyles/SalleManagement.css";

const emptyForm = {
    nom: "",
    code: "",
    type: "PHYSIQUE",
    color: "",
    description: "",
    maxPlaces: 0,
    tools: "",
};

const Modal = ({ open, title, onClose, footer, children }) => {
    if (!open) return null;

    return (
        <div className="for-modal-overlay" onMouseDown={onClose}>
            <div className="for-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="for-modal-header">
                    <h3>{title}</h3>
                    <button className="for-icon-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>
                <div className="for-modal-body">{children}</div>
                {footer ? <div className="for-modal-footer">{footer}</div> : null}
            </div>
        </div>
    );
};

export default function SalleManagement() {
    const dispatch = useDispatch();
    const { salles = [], loading } = useSelector((state) => state.salles || {});

    const [activeTab, setActiveTab] = useState("ALL");
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        dispatch(fetchSallesRequest());
    }, [dispatch]);

    const normalizedType = (type) => String(type || "").trim().toUpperCase();

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();

        return (salles || []).filter((salle) => {
            const type = normalizedType(salle.type);
            const matchesTab = activeTab === "ALL" || type === activeTab;
            const name = String(salle.nom || "").toLowerCase();
            const code = String(salle.code || "").toLowerCase();
            const matchesSearch = !query || name.includes(query) || code.includes(query);

            return matchesTab && matchesSearch;
        });
    }, [salles, activeTab, search]);

    const openAdd = () => {
        setIsEdit(false);
        setCurrentId(null);
        setForm(emptyForm);
        setOpen(true);
    };

    const openEdit = (salle) => {
        setIsEdit(true);
        setCurrentId(salle.id);
        setForm({
            nom: salle.nom || "",
            code: salle.code || "",
            type: normalizedType(salle.type) || "PHYSIQUE",
            color: salle.color || "",
            description: salle.description || "",
            maxPlaces: salle.maxPlaces ?? 0,
            tools: salle.tools || "",
        });
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setForm(emptyForm);
    };

    const onSubmit = () => {
        if (!form.code?.trim()) {
            Swal.fire({ icon: "warning", text: "Code obligatoire", timer: 1800, showConfirmButton: false });
            return;
        }

        if (!form.nom?.trim()) {
            Swal.fire({ icon: "warning", text: "Nom obligatoire", timer: 1800, showConfirmButton: false });
            return;
        }

        if (!form.type?.trim()) {
            Swal.fire({ icon: "warning", text: "Type obligatoire", timer: 1800, showConfirmButton: false });
            return;
        }

        const payload = {
            nom: form.nom,
            code: form.code,
            type: form.type,
            color: form.color,
            description: form.description,
            maxPlaces: Number(form.maxPlaces || 0),
            tools: form.tools,
        };

        if (isEdit && currentId) {
            dispatch(updateSalleRequest(currentId, payload));
        } else {
            dispatch(createSalleRequest(payload));
        }

        closeModal();
    };

    const onDelete = (id) => {
        Swal.fire({
            title: "Supprimer ?",
            text: "Cette action est irreversible",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Annuler",
        }).then((res) => {
            if (res.isConfirmed) {
                dispatch(deleteSalleRequest(id));
            }
        });
    };

    return (
        <div className="room-management-page for-page">
            <div className="room-management-header">
                <div className="room-management-header-info">
                    <h1>Gestion des salles</h1>
                </div>

                <div className="room-management-actions">
            <button className="room-management-btn room-management-btn-primary room-add-btn" onClick={openAdd}>
                <Plus size={16} /> Nouvelle Salle
            </button>
                </div>
            </div>

            <div className="room-management-filter-card">
                <div className="room-management-tabs">
                    <button
                        className={`room-management-tab ${activeTab === "ALL" ? "is-active" : ""}`}
                        onClick={() => setActiveTab("ALL")}
                    >
                        Toutes
                    </button>
                    <button
                        className={`room-management-tab ${activeTab === "PHYSIQUE" ? "is-active" : ""}`}
                        onClick={() => setActiveTab("PHYSIQUE")}
                    >
                        Physiques
                    </button>
                    <button
                        className={`room-management-tab ${activeTab === "VIRTUELLE" ? "is-active" : ""}`}
                        onClick={() => setActiveTab("VIRTUELLE")}
                    >
                        Virtuelles
                    </button>
                </div>

                <div className="room-management-search-wrap">
                    <Search size={15} className="room-management-search-icon" />
                    <input
                        className="room-management-search"
                        placeholder="Rechercher par nom ou code de salle..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="room-management-meta">
                    {loading ? "Chargement..." : `${filtered.length} salle(s) trouv?e(s)`}
                </div>
            </div>

            <div className="room-management-table-card">
                <div className="room-management-table-wrap">
                    <table className="room-management-table">
                        <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Type</th>
                                <th>Capacit?</th>
                                <th>Localisation / Code</th>
                                <th style={{ textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="room-management-empty-cell">
                                        {loading ? "Recherche en cours..." : "Aucune salle trouv?e"}
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((salle) => (
                                    <tr key={salle.id}>
                                        <td>
                                            <div className="room-management-title-cell">
                                                <div className="room-management-title">{salle.nom}</div>
                                                <div className="room-management-subtitle">
                                                    {salle.description || "Aucune description"}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span
                                                className={`room-management-badge ${
                                                    normalizedType(salle.type) === "VIRTUELLE"
                                                        ? "room-management-badge--virtual"
                                                        : "room-management-badge--physical"
                                                }`}
                                            >
                                                {normalizedType(salle.type) === "VIRTUELLE" ? (
                                                    <Monitor size={12} />
                                                ) : (
                                                    <MapPin size={12} />
                                                )}
                                                {normalizedType(salle.type) === "VIRTUELLE" ? "Virtuelle" : "Physique"}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="room-management-badge room-management-badge--blue">
                                                {salle.maxPlaces ?? "-"} places
                                            </span>
                                        </td>
                                        <td>
                                            <div className="room-management-location-cell">
                                                <div className="room-management-location-code">{salle.code || "-"}</div>
                                                <div className="room-management-location-meta">
                                                    {salle.color ? (
                                                        <>
                                                            <span
                                                                className="room-management-color-dot"
                                                                style={{ background: salle.color }}
                                                            />
                                                            <span>{salle.color}</span>
                                                        </>
                                                    ) : (
                                                        <span>Sans couleur</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="room-management-row-actions">
                                                <button
                                                    className="room-management-icon-btn"
                                                    title="Modifier"
                                                    onClick={() => openEdit(salle)}
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    className="room-management-icon-btn room-management-icon-btn--danger"
                                                    title="Supprimer"
                                                    onClick={() => onDelete(salle.id)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal
                open={open}
                title={isEdit ? "Edition de salle" : "Nouvelle salle"}
                onClose={closeModal}
                footer={
                    <>
                        <button className="for-btn for-btn-ghost" onClick={closeModal}>
                            Annuler
                        </button>
                        <button className="for-btn for-btn-primary" onClick={onSubmit}>
                            <Save size={16} /> {loading ? "Traitement..." : "Enregistrer"}
                        </button>
                    </>
                }
            >
                <div className="for-form-section">
                    <div className="for-form-title">Informations générales</div>
                    <div className="for-grid">
                        <div className="for-field">
                            <label>Nom de la salle *</label>
                            <input
                                placeholder="ex: Salle Newton"
                                value={form.nom}
                                onChange={(e) => setForm({ ...form, nom: e.target.value })}
                            />
                        </div>

                        <div className="for-field">
                            <label>Code interne *</label>
                            <input
                                placeholder="ex: S-01"
                                value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value })}
                            />
                        </div>

                        <div className="for-field">
                            <label>Type d'espace *</label>
                            <select
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                            >
                                <option value="PHYSIQUE">Physique </option>
                                <option value="VIRTUELLE">Virtuelle</option>
                            </select>
                        </div>

                        <div className="for-field">
                            <label>Capacité maximale *</label>
                            <input
                                type="number"
                                min={0}
                                placeholder="ex: 15"
                                value={form.maxPlaces}
                                onChange={(e) => setForm({ ...form, maxPlaces: e.target.value })}
                            />
                        </div>

                        <div className="for-field">
                            <label>Couleur d'identification</label>
                            <input
                                type="color"
                                value={form.color}
                                onChange={(e) => setForm({ ...form, color: e.target.value })}
                            />
                        </div>
                    </div>
                </div>

                <div className="for-form-section">
                    <div className="for-form-title">Détails logistiques</div>
                    <div className="for-grid">
                        <div className="for-field for-field--full">
                            <label>Equipements et outils</label>
                            <input
                                placeholder="ex: Videoprojecteur, Tableau blanc, Zoom, Teams..."
                                value={form.tools}
                                onChange={(e) => setForm({ ...form, tools: e.target.value })}
                            />
                        </div>

                        <div className="for-field for-field--full">
                            <label>Description complémentaire</label>
                            <textarea
                                rows={3}
                                placeholder="Localisation, instructions d'accés..."
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                            />
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
